<template>

  <v-container class="pa-0 ma-0 step delivery-step-head-to-receiver" v-if="route && orders && receiverLocation">

    <!-- Receiver info -->
    <v-row class="pa-0 ma-0">
      <v-col sm="12" style="font-size:18px;" class="ma-0 pa-0 content-accent">{{ receiverLocation.name }}</v-col>
    </v-row>
    <v-row class="pa-0 ma-0">
      <v-col cols="12" class="ma-0 pa-0 pt-2 address-text d-flex align-center">
        <div>
          <div>{{ formatAddress(receiverLocation).street_1 }}</div>
          <div v-if="formatAddress(receiverLocation).street_2">{{ formatAddress(receiverLocation).street_2 }}</div>
          <div v-if="formatAddress(receiverLocation).area">{{ formatAddress(receiverLocation).area }}</div>
        </div>
        <v-btn plain color="primary" class="ml-6" @click="copyAddress">
          <v-icon class="icon-copy">mdi-content-copy</v-icon>
        </v-btn>
      </v-col>
    </v-row>

    <!-- Heading to receiver button -->
    <v-row class="pa-0 ma-0 mt-6">
      <v-col sm="12" class="pa-0 ma-0 d-flex flex-column align-left justify-center">
        <v-btn color="primary" v-if="isToReceiverProcessing">
          <v-progress-circular indeterminate :size="20" color="white" />
        </v-btn>
        <v-btn
            large
            color="primary"
            :disabled="true"
            v-else-if="hasCompletedHeadingToReceiver">
          En route at {{ formatDateTime(hasCompletedHeadingToReceiver) }}
        </v-btn>
        <double-click-confirm-button
            v-else
            buttonText="Heading to receiver"
            confirmTitle="Ready to head to the receiver?"
            :confirmMessageText="(isTestRoute) ? '' : 'This will notify all relevant parties.'"
            :disabled="isHeadingToReceiver || isAtReceiver"
            @confirmed="headToReceiver" />
      </v-col>
    </v-row>
    <v-row class="pa-0 ma-0">
      <v-col sm="12" class="ma-0 pa-0 mt-2 instructions-text" v-if="showDrivingDirections">
        (click to get directions)
      </v-col>
    </v-row>

    <!-- directions link -->
    <v-row class="pa-0 ma-0 mt-3">
      <v-col sm="12" class="ma-0 pa-0">
        <v-btn text color="primary" :disabled="!isHeadingToReceiver" v-if="showDrivingDirections">
          <v-icon class="icon-open-in-new">mdi-open-in-new</v-icon>
          <span>Driving directions</span>
        </v-btn>
      </v-col>
    </v-row>

    <!-- Order info -->
    <v-row class="pa-0 ma-0 mt-6">
      <v-col sm="12" class="ma-0 pa-0 content-title">
        <span v-if="orders.length > 1">Deliver orders:</span>
        <span v-else>Deliver order:</span>
        <span style="font-size:1.2rem;" class="ml-1 content-accent">{{ orderNumbersForDisplay }}</span>
      </v-col>
    </v-row>
    <v-row class="pa-0 ma-0 mt-1">
      <v-col sm="12" class="ma-0 pa-0 content-title">
        To: <span class="content-accent">{{ receiverLocation.name }}</span>
      </v-col>
    </v-row>

    <!-- Complete step -->
    <v-row class="complete-step">
      <v-col sm="12" class="pa-0 ma-0 mt-2 d-flex flex-column justify-center">
        <v-btn color="primary" v-if="isAtReceiverProcessing">
          <v-progress-circular indeterminate :size="20" color="white" />
        </v-btn>
        <v-btn
            large
            color="primary"
            :disabled="true"
            v-else-if="hasCompletedArrivedAtReceiver">
          Arrived at {{ formatDateTime(hasCompletedArrivedAtReceiver) }}
        </v-btn>
        <double-click-confirm-button
            v-else
            buttonText="Arrived"
            confirmTitle="Arrived at the receiver?"
            :confirmMessageText="(isTestRoute) ? '' : 'This will notify all relevant parties.'"
            :disabled="!isHeadingToReceiver"
            @confirmed="completeStep" />
      </v-col>
    </v-row>
  </v-container>

</template>

<script>
import DoubleClickConfirmButton from '@/components/app/DoubleClickConfirmButton'

export default {
  name: 'DeliveryStepHeadToReceiver',

  components: {
    DoubleClickConfirmButton
  },

  props: {
    route: {
      type: Object,
      required: true
    },
    orders: {
      type: Array,
      required: true
    },
    stepNumber: {
      type: Number,
      required: true
    }
  },

  data () {
    return {
      showDrivingDirections: false,
      isToReceiverProcessing: false,
      isAtReceiverProcessing: false
    }
  },

  computed: {
    isTestRoute () {
      return this.route.is_test
    },
    firstOrder () {
      return (this.orders.length > 0) ? this.orders[0] : {}
    },
    orderNumbersForDisplay () {
      return (this.orders.length > 0)
        ? this.orders.map(order => ` ${order.thirdparty_order_number}`).toString()
        : ''
    },
    receiverLocation () {
      return this.firstOrder.receiver
    },
    isHeadingToReceiver () {
      return this.firstOrder.order_status === 'TO_RECEIVER'
    },
    isAtReceiver () {
      return this.firstOrder.order_status === 'AT_RECEIVER'
    },
    hasCompletedHeadingToReceiver () {
      return this.firstOrder.to_receiver_at
    },
    hasCompletedArrivedAtReceiver () {
      return this.firstOrder.at_receiver_at
    }
  },

  methods: {
    // copy address to clipboard
    copyAddress () {
      this.$root.copyToClipboard(this.$root.utils.getAddressTextFromData(this.receiverLocation))
    },

    // Display a date/time
    formatDateTime (date) {
      return (date) ? this.$root.utils.dateFormat(date, 'MMM D h:mma') : ''
    },

    formatAddress (data = {}) {
      return this.$root.utils.getAddressTextFromData(data, { multi_lines: true })
    },

    // Set orders: to receiver
    async headToReceiver () {
      this.isToReceiverProcessing = true

      const orderIds = this.orders.map(it => it.id)

      try {
        const result = await this.$store.dispatch('sh/dataApi', {
          method: 'post',
          uri: 'orders/to-receiver',
          opts: {
            bodyData: { order_ids: orderIds }
          },
          to_model: false
        })
        const updatedOrders = result.updated || []
        // Sync into data registries
        for (const orderData of updatedOrders) {
          const order = this.$root.toModel(orderData)
          this.$store.dispatch('sh/syncData', { resource: 'activeDelivery', nested: 'orders', data: order })
          this.$store.dispatch('sh/syncItemEditing', { resource: 'activeDelivery', nested: 'orders', data: order })
        }
      } catch (err) {
        console.error('Error setting orders: to-receiver', err)
      } finally {
        this.isToReceiverProcessing = false
      }
    },

    // Set orders: at receiver and complete step
    async completeStep () {
      this.isAtReceiverProcessing = true
      const orderIds = this.orders.map(it => it.id)

      try {
        const result = await this.$store.dispatch('sh/dataApi', {
          method: 'post',
          uri: 'orders/at-receiver',
          opts: {
            bodyData: { order_ids: orderIds }
          },
          to_model: false
        })
        const updatedOrders = result.updated || []
        // Sync into data registries
        for (const orderData of updatedOrders) {
          const order = this.$root.toModel(orderData)
          this.$store.dispatch('sh/syncData', { resource: 'activeDelivery', nested: 'orders', data: order })
          this.$store.dispatch('sh/syncItemEditing', { resource: 'activeDelivery', nested: 'orders', data: order })
        }

        // Emit step completed
        this.$emit('completed')
      } catch (err) {
        console.error('Error setting orders: at-receiver', err)
      } finally {
        this.isAtReceiverProcessing = false
      }
    }
  }
}
</script>

<style>
</style>
